import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';

const PACIAuthDemo = ({ onAuthSuccess, onAuthCancel }) => {
  const [step, setStep] = useState('input'); // 'input', 'qr', 'waiting', 'success'
  const [civilId, setCivilId] = useState('');
  const [qrValue, setQrValue] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [demoMode, setDemoMode] = useState(true); // This is a demo/mockup

  // Generate mock QR code for demo
  const generateMockQR = (civilId) => {
    const timestamp = Date.now();
    return `ENAYAHUB_DEMO_${civilId}_${timestamp}`;
  };

  // Simulate QR code generation
  const handleGenerateQR = () => {
    if (civilId.length >= 10) {
      const mockQRValue = generateMockQR(civilId);
      setQrValue(mockQRValue);
      setStep('qr');
      setCountdown(60);
      
      // Start countdown timer
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setStep('input');
            return 60;
          }
          return prev - 1;
        });
      }, 1000);

      // Demo: Auto-success after 10 seconds
      setTimeout(() => {
        clearInterval(timer);
        setStep('success');
        setTimeout(() => {
          onAuthSuccess && onAuthSuccess({
            civilId: civilId,
            name: 'أحمد محمد الكندري', // Demo Arabic name
            nameEn: 'Ahmed Mohammed Al-Kandari',
            nationality: 'Kuwaiti',
            verified: true,
            verificationLevel: 'High'
          });
        }, 2000);
      }, 10000);
    }
  };

  // Handle push notification demo
  const handlePushNotification = () => {
    if (civilId.length >= 10) {
      setStep('waiting');
      
      // Demo: Auto-success after 5 seconds
      setTimeout(() => {
        setStep('success');
        setTimeout(() => {
          onAuthSuccess && onAuthSuccess({
            civilId: civilId,
            name: 'أحمد محمد الكندري',
            nameEn: 'Ahmed Mohammed Al-Kandari',
            nationality: 'Kuwaiti',
            verified: true,
            verificationLevel: 'High'
          });
        }, 2000);
      }, 5000);
    }
  };

  const formatCivilId = (value) => {
    // Remove non-digits and limit to 12 characters
    const digits = value.replace(/\D/g, '').slice(0, 12);
    // Add spacing for better readability: 000-000000-00-0
    if (digits.length >= 3) {
      return digits.replace(/(\d{3})(\d{6})(\d{2})(\d{1})/, '$1-$2-$3-$4');
    }
    return digits;
  };

  return (
    <div className="paci-auth-demo" style={{
      maxWidth: '400px',
      margin: '0 auto',
      padding: '24px',
      border: '2px solid #0066CC',
      borderRadius: '12px',
      backgroundColor: '#f8fafc',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>🇰🇼</div>
        <h3 style={{ margin: '0', color: '#0066CC', fontSize: '18px' }}>
          هويتي - PACI Authentication
        </h3>
        {demoMode && (
          <div style={{ 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            padding: '8px 12px', 
            borderRadius: '6px',
            fontSize: '12px',
            marginTop: '8px'
          }}>
            🚧 Demo Mode - Simulated Authentication
          </div>
        )}
      </div>

      {/* Step 1: Civil ID Input */}
      {step === 'input' && (
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: 'bold',
            color: '#374151'
          }}>
            Civil ID Number / رقم الهوية المدنية
          </label>
          <input
            type="text"
            value={civilId}
            onChange={(e) => setCivilId(formatCivilId(e.target.value))}
            placeholder="000-000000-00-0"
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '16px',
              textAlign: 'center',
              letterSpacing: '2px',
              fontFamily: 'monospace'
            }}
            maxLength={15} // Including dashes
          />
          
          <div style={{ marginTop: '16px' }}>
            <button
              onClick={handleGenerateQR}
              disabled={civilId.length < 10}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: civilId.length >= 10 ? '#0066CC' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: civilId.length >= 10 ? 'pointer' : 'not-allowed',
                marginBottom: '8px'
              }}
            >
              📱 Generate QR Code
            </button>
            
            <button
              onClick={handlePushNotification}
              disabled={civilId.length < 10}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: civilId.length >= 10 ? '#059669' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: civilId.length >= 10 ? 'pointer' : 'not-allowed'
              }}
            >
              🔔 Send Push Notification
            </button>
          </div>
        </div>
      )}

      {/* Step 2: QR Code Display */}
      {step === 'qr' && (
        <div style={{ textAlign: 'center' }}>
          <h4 style={{ color: '#374151', marginBottom: '16px' }}>
            Scan with هويتي App
          </h4>
          
          <div style={{ 
            backgroundColor: 'white', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <QRCode 
              value={qrValue}
              size={200}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>
          
          <div style={{ 
            backgroundColor: '#e0e7ff', 
            padding: '12px', 
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <p style={{ margin: '0', fontSize: '14px', color: '#374151' }}>
              ⏰ Code expires in: <strong>{countdown}s</strong>
            </p>
          </div>
          
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            1. Open هويتي app on your phone<br/>
            2. Tap "Scan QR Code"<br/>
            3. Approve the authentication request
          </p>
          
          <button
            onClick={() => setStep('input')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Step 3: Waiting for Push Notification */}
      {step === 'waiting' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            📱
          </div>
          <h4 style={{ color: '#374151', marginBottom: '16px' }}>
            Check your هويتي App
          </h4>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            A push notification has been sent to your device.<br/>
            Please approve the authentication request.
          </p>
          
          {/* Loading animation */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #0066CC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
          
          <button
            onClick={() => setStep('input')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 'success' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            ✅
          </div>
          <h4 style={{ color: '#059669', marginBottom: '16px' }}>
            Authentication Successful!
          </h4>
          
          <div style={{ 
            backgroundColor: '#ecfdf5', 
            border: '1px solid #10b981',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            textAlign: 'left'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
              <strong>Name:</strong> أحمد محمد الكندري
            </p>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
              <strong>Name (EN):</strong> Ahmed Mohammed Al-Kandari
            </p>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
              <strong>Civil ID:</strong> {civilId}
            </p>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
              <strong>Nationality:</strong> Kuwaiti 🇰🇼
            </p>
            <p style={{ margin: '0', fontSize: '14px' }}>
              <strong>Verification Level:</strong> High ⭐
            </p>
          </div>
          
          <p style={{ fontSize: '12px', color: '#6b7280' }}>
            Account will be created automatically...
          </p>
        </div>
      )}

      {/* CSS for loading animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PACIAuthDemo;